import { toast } from "react-toastify";

function isGastoraLicense(license) {
  const key = String(license?.LisansKey || "");
  return key.startsWith("GST") || license?.UrunAilesi === "Gastora" || license?.UrunAilesi === "Gastro";
}

/** Lisans bilgisi görseli: "Bulut Sunucu" | "Yerel Sunucu" */
export function formatServerTypeLabel(license, hasBulutSunucu) {
  const raw = String(
    license?.KurulumTipi || license?.SunucuTipi || license?.serverType || "",
  )
    .trim()
    .toLocaleUpperCase("tr-TR");
  if (!raw) {
    if (hasBulutSunucu === true) return "Bulut Sunucu";
    return "Yerel Sunucu";
  }
  if (raw === "CLOUD" || raw === "BULUT" || raw.includes("BULUT")) {
    return "Bulut Sunucu";
  }
  if (raw === "LOCAL" || raw === "YEREL" || raw.includes("YEREL") || raw.includes("LOCAL")) {
    return "Yerel Sunucu";
  }
  return "Yerel Sunucu";
}

const GASTRO_ADDON_LABELS = {
  QR_MENU: "QR Menü",
  QR: "QR Menü",
  QRMENU: "QR Menü",
  PAKET_SERVIS: "Paket Servis",
  PAKET: "Paket Servis",
  URETIM: "Üretim",
  YEMEKSEPETI: "Yemek Sepeti",
  GETIR: "Getir Yemek",
  GETIR_YEMEK: "Getir Yemek",
  TRENDYOL: "Trendyol Yemek",
  TRENDYOL_YEMEK: "Trendyol Yemek",
  MIGROS: "Migros Yemek",
  MIGROS_YEMEK: "Migros Yemek",
  SADAKAT: "Sadakat",
};

/** Ana Gastro paketi — ek modül satış listesinde yer almaz */
const GASTRO_CORE_EK_EXCLUDE = new Set(["RESTORAN", "GASTRO"]);

function normalizeGastroAddonCode(code) {
  const c = String(code || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_]/g, "_");
  if (!c) return "";
  if (c === "QR" || c === "QRMENU") return "QR_MENU";
  if (c === "PAKET" || c === "PAKETSERVIS") return "PAKET_SERVIS";
  if (c === "GETIR" || c === "GETIRYEMEK") return "GETIR_YEMEK";
  if (c === "TRENDYOL" || c === "TRENDYOLYEMEK") return "TRENDYOL_YEMEK";
  if (c === "MIGROS" || c === "MIGROSYEMEK") return "MIGROS_YEMEK";
  return c;
}

/** Sertifika: siparişte seçilen ek modüller (Restoran/Gastro ana paket — listede yok) */
function gastroEkModulesFromLicense(license) {
  if (Array.isArray(license?.PurchasedEkModuller) && license.PurchasedEkModuller.length) {
    return license.PurchasedEkModuller.map((m) => ({
      ProgramAdi: m.Adi || m.ProgramAdi || m.name || String(m),
    }));
  }

  let codes = [];
  try {
    const parsed = JSON.parse(license?.enabledAddonsJson || "[]");
    if (Array.isArray(parsed)) codes = parsed;
  } catch {
    codes = [];
  }

  const seen = new Set();
  const out = [];
  for (const raw of codes) {
    const norm = normalizeGastroAddonCode(raw);
    if (!norm || GASTRO_CORE_EK_EXCLUDE.has(norm) || seen.has(norm)) continue;
    seen.add(norm);
    out.push({
      ProgramAdi: GASTRO_ADDON_LABELS[norm] || String(raw).trim(),
    });
  }
  return out;
}

export function gastoraModulesDisplay(license) {
  const core = [
    license?.moduleQrMenu && "QR",
    license?.moduleRestoran && "Restoran",
    license?.modulePaketServis && "Paket",
    license?.moduleUretim && "Üretim",
  ].filter(Boolean);
  const addons = gastroEkModulesFromLicense(license).map((m) => m.ProgramAdi);
  return [...core, ...addons].filter(Boolean).join(", ") || "—";
}

export function erpEkModulesDisplay(license, fallbackEkModule) {
  const list = modulesForCertificate(license, fallbackEkModule);
  return list.map((m) => m.ProgramAdi).filter(Boolean).join(", ") || "—";
}

export function modulesForCertificate(license, fallbackEkModule) {
  if (isGastoraLicense(license)) {
    return gastroEkModulesFromLicense(license);
  }
  if (Array.isArray(license?.EkModuller) && license.EkModuller.length > 0) {
    return license.EkModuller.map((m) => ({
      ProgramAdi: m.ProgramAdi || m.Adi || m.name || String(m),
    }));
  }
  if (Array.isArray(fallbackEkModule) && fallbackEkModule.length > 0) {
    return fallbackEkModule.map((m) => ({
      ProgramAdi: m.ProgramAdi || m.Adi || m.name || String(m),
    }));
  }
  return [];
}

export const handleCertificateGenerate = async (
  license,
  userData,
  customerPackage,
  customer,
  customerEkModule,
  navigate,
  hasBulutSunucu,
) => {
  if (!userData) {
    toast.error("Yetkilendirme hatası: Token bulunamadı.");
    return;
  }
  const gastora = isGastoraLicense(license);
  const endDate = license.BitisTarih ? new Date(license.BitisTarih) : new Date();
  const startDate = license.BaslangicTarih
    ? new Date(license.BaslangicTarih)
    : new Date(endDate);
  if (!license.BaslangicTarih) {
    startDate.setFullYear(endDate.getFullYear() - 1);
  }

  const formattedDate =
    startDate.toLocaleDateString("tr-TR") + " - " + endDate.toLocaleDateString("tr-TR");
  const licensePackageName =
    license.PaketAdi || license.Paket?.Adi || customerPackage?.Adi || "";
  const licenseEkModules = modulesForCertificate(license, customerEkModule);
  const rawUsers = license.UserCount ?? license.MaxKullaniciSayisi;
  const userCount = Math.max(0, Number(rawUsers || 0) - 1);
  const serverType = formatServerTypeLabel(license, hasBulutSunucu);

  const licenseNo = customer.MusteriNo || customer.Id || "";

  try {
    navigate("/sertifika", {
      state: {
        date: formattedDate,
        packageName: licensePackageName,
        licenseNo,
        company: customer.Unvan,
        customerEmail: customer.EPosta,
        uid: gastora ? license.LisansKey || customer.UID : customer.UID,
        ekModules: licenseEkModules,
        startDate: startDate.toLocaleDateString("tr-TR"),
        endDate: endDate.toLocaleDateString("tr-TR"),
        userCount,
        productFamily: gastora ? "gastro" : "pozitif",
        serverType,
      },
    });
  } catch (error) {
    toast.error("Müşteri verileri alınamadı");
  }
};
